import { useEffect, useState } from 'react';
import { EstadoSinPermiso } from '../../componentes/EstadoPagina';
import { Segmentos } from '../../componentes/Segmentos';
import { rolesDe } from '../../logica/acceso';
import { usePermisos } from '../../permisos/permisos';
import { rutaConApartado, type PropsPantalla } from '../tipos';
import { Conversaciones } from './Conversaciones';
import { Llamadas } from './Llamadas';
import type { Quien } from './reglas';
import '../../componentes/apartado.css';
import '../avisos/avisos.css';
import './buzon.css';

/**
 * «Buzón» (Comunicación, entrada `buzon`): los apartados Conversaciones (11.3) y Llamadas (11.9, Voz IA «próximamente»)
 * en `?apartado=` (`llamadas`; sin él, Conversaciones), como en el diseño (`11.3-11.9-buzon-y-voz.html`). La entrada no
 * declara apartados en el servidor: la barra es de la propia pantalla, como en «Web pública».
 *
 * Quién ve qué (G87): con `canales.buzon.atender` con «todo», todas; con «lo suyo» (Recepción / Puerta), solo las
 * asignadas a esa persona y las que esperan a una persona. Sin `canales.conversaciones.ver`, «Sin permiso».
 */
const APARTADOS = [
    { valor: 'conversaciones', etiqueta: 'Conversaciones', icono: 'message-circle' },
    { valor: 'llamadas', etiqueta: 'Llamadas', icono: 'phone' },
] as const;

export function Buzon(props: PropsPantalla) {
    const { busqueda, ruta, navegar, movil, membresia, nombreNegocio, ops, negocio } = props;
    const permisos = usePermisos();
    // Bloques vigentes (`listEntitlements`, permiso `negocio.ver`, todos los roles): sin Asistente IA, el buzón sin filtro
    // «Con el asistente», sin «Devolver» ni «Fijar» (G453). Si no se puede leer, como si lo tuviera (la API responde 409).
    const [asistente, setAsistente] = useState(true);
    useEffect(() => {
        let vivo = true;
        ops.listEntitlements(negocio)
            .then((b) => { if (vivo) setAsistente(b.entitlements.some((e) => e.product_code === 'asistente_ia')); })
            .catch(() => undefined);
        return () => { vivo = false; };
    }, [ops, negocio]);
    const ambito = permisos.ambito('canales.buzon.atender') ?? permisos.ambito('canales.conversaciones.ver');
    const quien: Quien = {
        todas: ambito === 'all',
        suyas: ambito === 'own',
        yo: membresia?.member_id ?? null,
        configura: permisos.puede('canales.buzon.configurar'),
        asistente,
    };

    if (!permisos.puede('canales.conversaciones.ver') || ambito === null) {
        return <EstadoSinPermiso seccion="Buzón" rol={membresia === undefined ? '' : rolesDe(membresia)} negocio={nombreNegocio} onInicio={() => { navegar('/'); }} movil={movil} />;
    }

    const activo = busqueda.get('apartado') === 'llamadas' ? 1 : 0;
    const base = ruta.split('?')[0] ?? ruta;

    return (
        <div className="rv-en-pantalla rv-bz">
            <div className="rv-en-barra rv-bz-barra">
                <Segmentos
                    etiqueta="Apartados del buzón"
                    movil={movil}
                    items={APARTADOS.map((a) => ({ etiqueta: a.etiqueta, icono: a.icono }))}
                    valor={activo}
                    onCambio={(i) => {
                        navegar(rutaConApartado(base, i === 1 ? 'llamadas' : null));
                    }}
                />
            </div>
            {activo === 1 ? <Llamadas movil={movil} configura={permisos.puede('canales.canales.gestionar')} /> : <Conversaciones {...props} quien={quien} />}
        </div>
    );
}
